import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, MapPin, ShieldCheck, ThumbsDown, Trophy, User2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

// Owner-facing check-in validation page.
// A camper generates a pending check-in via CheckinDialog → they show a QR
// containing the raw token. The owner (or their staff) either scans the QR
// which opens this route, OR types the token into a URL manually.
//
// On mount we GET /checkins/preview/{token} and render the camper + stay
// details. The owner can then POST /checkins/validate (mints stamp + EXP)
// or /checkins/reject (marks the doc rejected).
export default function CheckinPreview() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { api, user, isOwner, isCamper } = useAuth();

  const [checkin, setCheckin] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);           // "validated" | "rejected"
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setError("Missing token."); return; }
    api.get(`/checkins/preview/${token}`)
      .then(({ data }) => { if (!cancelled) setCheckin(data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not load this check-in.");
      });
    return () => { cancelled = true; };
  }, [api, token]);

  const validate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/checkins/validate", { checkin_token: token });
      setCheckin(data);
      setDone("validated");
      toast.success("Check-in validated — stamp issued.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not validate.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/checkins/reject", {
        checkin_token: token,
        reason: rejectReason.trim() || null,
      });
      setDone("rejected");
      toast.success("Check-in rejected.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not reject.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center card-elev p-8">
        <div className="text-lg font-extrabold mb-2">Sign in to view this check-in</div>
        <Link to={`/login?next=${encodeURIComponent(`/checkin/preview/${token}`)}`} className="btn btn-green">
          Sign in
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto py-16">
        <div className="card-elev p-8 text-center">
          <div className="text-lg font-extrabold mb-2" style={{ color: "var(--on-surface)" }}>
            Can't open this check-in
          </div>
          <div className="text-sm mb-6" style={{ color: "var(--on-surface-muted)" }}>{error}</div>
          <button onClick={() => navigate(isOwner ? "/owner" : "/passport")} className="btn btn-green">
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!checkin) {
    return (
      <div className="max-w-lg mx-auto py-16 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--brand)" }} />
      </div>
    );
  }

  const statusPill = () => {
    const s = checkin.status;
    const map = {
      pending:   { bg: "color-mix(in srgb, var(--premium) 12%, transparent)", fg: "var(--premium)", label: "Pending" },
      validated: { bg: "color-mix(in srgb, var(--brand) 12%, transparent)",   fg: "var(--brand)",   label: "Validated" },
      rejected:  { bg: "color-mix(in srgb, #B45309 12%, transparent)",        fg: "#B45309",         label: "Rejected" },
    };
    const style = map[s] || map.pending;
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
            style={{ background: style.bg, color: style.fg }} data-testid="preview-status">
        {style.label}
      </span>
    );
  };

  const isPending = checkin.status === "pending" && !done;
  const canAct = isOwner; // camper preview allowed for own doc but no action

  return (
    <div className="max-w-lg mx-auto flex flex-col gap-5" data-testid="checkin-preview-page">
      <div>
        <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Check-in preview
        </div>
        <div className="flex items-center justify-between mt-1">
          <h1 className="text-2xl md:text-3xl font-extrabold" style={{ color: "var(--on-surface)" }}>
            {checkin.campsite_name}
          </h1>
          {statusPill()}
        </div>
        <div className="mt-1 text-sm inline-flex items-center gap-1" style={{ color: "var(--on-surface-muted)" }}>
          <MapPin className="w-3.5 h-3.5" /> {checkin.campsite_state}
        </div>
      </div>

      {/* Camper card */}
      <section className="card-elev p-5 flex items-center gap-4" data-testid="preview-camper">
        <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-extrabold shrink-0"
             style={{ background: "var(--brand)", color: "white" }}>
          {(checkin.camper_name?.[0] || "C").toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-extrabold" style={{ color: "var(--on-surface)" }}>{checkin.camper_name}</div>
          <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>{checkin.camper_email || "—"}</div>
          {checkin.camper_level != null && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full"
                 style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>
              <Trophy className="w-3 h-3" /> Lvl {checkin.camper_level} · {checkin.camper_title || "Newbie Camper"}
            </div>
          )}
        </div>
      </section>

      {/* Stay card */}
      <section className="card-elev p-5 flex flex-col gap-3" data-testid="preview-stay">
        <Row label="Check-in"  value={(checkin.checkin_date || "").slice(0, 10)} />
        <Row label="Check-out" value={(checkin.checkout_date || "").slice(0, 10)} />
        <Row label="Backdated" value={checkin.backdated ? "Yes · half XP" : "No"} />
        {checkin.remarks && <Row label="Camper note" value={checkin.remarks} />}
      </section>

      {/* Action buttons — owner only */}
      {isPending && canAct && (
        <section className="flex flex-col gap-3">
          <button onClick={validate} disabled={busy} className="btn btn-green justify-center w-full"
                  data-testid="preview-validate">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Validating…</> : <><CheckCircle2 className="w-4 h-4" /> Validate & issue stamp</>}
          </button>
          {!showRejectForm ? (
            <button onClick={() => setShowRejectForm(true)} className="btn btn-light justify-center w-full"
                    data-testid="preview-reject-open">
              <ThumbsDown className="w-4 h-4" /> Reject
            </button>
          ) : (
            <div className="card-elev p-4 flex flex-col gap-3" data-testid="preview-reject-form">
              <label className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
                Reason (optional, shown to the camper)
                <textarea
                  value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                  rows={3} maxLength={280} className="w-full mt-2 px-3 py-2.5 rounded-xl border text-sm resize-none font-normal normal-case tracking-normal"
                  style={{ background: "var(--card)", borderColor: "var(--border-c)", color: "var(--on-surface)" }}
                  data-testid="preview-reject-reason"
                />
              </label>
              <div className="flex gap-2">
                <button onClick={reject} disabled={busy} className="btn btn-light flex-1 justify-center"
                        style={{ color: "#B45309" }} data-testid="preview-reject-confirm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reject"}
                </button>
                <button onClick={() => setShowRejectForm(false)} className="btn btn-light flex-1 justify-center">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {isPending && !canAct && (
        <div className="card-elev p-4 text-sm" style={{ color: "var(--on-surface-muted)" }}>
          Waiting for the campsite owner to validate this stamp. This page will update automatically.
        </div>
      )}

      {done === "validated" && (
        <div className="card-elev p-5 text-center" data-testid="preview-done-validated">
          <CheckCircle2 className="w-10 h-10 mx-auto" style={{ color: "var(--brand)" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>
            Stamp issued
          </div>
          <div className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
            {checkin.camper_name} earned {checkin.exp_earned || 0} XP.
          </div>
        </div>
      )}
      {done === "rejected" && (
        <div className="card-elev p-5 text-center" data-testid="preview-done-rejected">
          <X className="w-10 h-10 mx-auto" style={{ color: "#B45309" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>Rejected</div>
          <div className="text-sm mt-1" style={{ color: "var(--on-surface-muted)" }}>
            The camper has been notified.
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>{label}</div>
      <div className="text-sm text-right" style={{ color: "var(--on-surface)" }}>{value}</div>
    </div>
  );
}
